import { Component, ComponentRef, Injector, ViewChild, ViewContainerRef } from '@angular/core';
import { DialogBaseComponent } from "../../base-admin-panel/dialog-base/dialog-base.component";
import { NotificationViewModel } from "../Model/NotificationViewModel";
import { NotificationsService } from "../../../../services/notifications.service";
import { CommandsService, CommandTemplateModel } from "../../../../services/commands.service";
import { IManagableObject } from "../../IManagableObject";
import { ObjectsService } from "../../../../services/objects.service";
import { ObjectGroupViewModel } from '../../ObjectGroupViewModel';
import { DialogService } from '../../../../services/dialog.service';
import { ObjectsManagementDialogComponent } from '../../objects-management-dialog/objects-management-dialog.component';
import { ZoneService } from '../../../../services/zones.service';
import { GeozoneViewModel } from '../../Geozone/Model/GeozoneViewModel';
import { ContainersService } from 'app/services/containers.service';
import { ContainerViewModel } from '../../ContainerViewModel';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-notifications-dialog-base',
    templateUrl: './notifications-dialog-base.component.html',
    styleUrls: ['./notifications-dialog-base.component.css'],
    standalone: false
})
export class NotificationsDialogBaseComponent extends DialogBaseComponent<NotificationViewModel> {

  @ViewChild("objectsListDialog", { read: ViewContainerRef, static: true }) objectsListContainer;
  private objectsListDialog: ComponentRef<any>;

  public groups: IManagableObject[] = [];

  protected service: NotificationsService;
  private commandsService: CommandsService;
  private objectsService: ObjectsService;
  private geozoneService: ZoneService;
  private containersService: ContainersService;
  private dialogService: DialogService;

  private geozoneGroups: ObjectGroupViewModel[] = [];
  private geozones: GeozoneViewModel[] = [];

  public commands: CommandTemplateModel[] = [];
  public containers: ContainerViewModel[] = [];

  public allObjects: IManagableObject[] = [];
  public objectGroups: ObjectGroupViewModel[] = [];

  public openedTabName: string = "main";
  public isLoading: boolean = false;

  constructor(protected injector: Injector) {
    super(injector);
    this.service = injector.get(NotificationsService);
    this.commandsService = injector.get(CommandsService);
    this.objectsService = injector.get(ObjectsService);
    this.dialogService = injector.get(DialogService);
    this.geozoneService = injector.get(ZoneService);
    this.containersService = injector.get(ContainersService);
  }

  public openTab(name: string) {
    this.openedTabName = name;
  }
  public isSelected(name: string) {
    return name == this.openedTabName;
  }

  open(model: NotificationViewModel, type: { new(): NotificationViewModel } = undefined) {
    super.open(model, type);
    this.isLoading = true;

    forkJoin({
      containers: this.containersService.GetContainers(),
      commands: this.commandsService.getCommands(),
      groups: this.service.GetGroups(),
      allObjects: this.objectsService.GetObjects(),
      objectGroups: this.objectsService.GetObjectGroups(),
      geozoneGroups: this.geozoneService.GetGroups(),
      geozones: this.geozoneService.GetZones()
    }).subscribe(result => {
      this.containers = [...(result.containers ?? [])]
        .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));
      this.ensureContainerSelected();
      this.commands = result.commands;
      this.groups = result.groups;
      this.allObjects = result.allObjects;
      this.objectGroups = result.objectGroups;
      this.geozoneGroups = result.geozoneGroups;
      this.geozones = result.geozones;
      this.syncSelectionsWithContainer();
      this.isLoading = false;
    }, () => {
      this.isLoading = false;
    });
  }

  public selectGeozones(ids: number[]): void {
    this.objectsListDialog = this.dialogService.open(this.objectsListContainer, ObjectsManagementDialogComponent);

    this.objectsListDialog.instance.groups = this.filteredGeozoneGroups;
    this.objectsListDialog.instance.objects = this.filteredGeozones.map(zone => Object.assign({}, zone));
    for (var i = 0; i < this.objectsListDialog.instance.objects.length; i++)
      this.objectsListDialog.instance.objects[i].IsChecked = ids.includes(this.objectsListDialog.instance.objects[i].Id);

    this.objectsListDialog.instance.onSubmit.subscribe(() => {
      ids.splice(0, ids.length);
      this.objectsListDialog.instance.objects.forEach(zone => {
        if (zone.IsChecked)
          ids.push(zone.Id);
      });

      this.objectsListDialog.instance.close();
    });

    this.objectsListDialog.instance.open();
  }
  public getGeozonesDescription(ids: number[]): string {
    var res = "";

    for (var i = 0; i < this.filteredGeozones.length; i++)
      if (ids.includes(this.filteredGeozones[i].Id))
        res += ((res.length > 0) ? "," : "") + this.filteredGeozones[i].Name;

    return res;
  }
  public CleanInGeozones(event) {
    this.model.InGeozones = [];
    event.stopPropagation();
    return false;
  }
  public CleanOutGeozones(event) {
    this.model.OutOfGeozones = [];
    event.stopPropagation();
    return false;
  }
  public convertRepeatPeriod(value: number) {
    this.model.RepeatPeriod = value * 3600000;
  }
  public convertMinDurationPeriod(value: number) {
    this.model.MinDuration = value * 1000;
  }

  public convertMinDelayBetween(value: number) {
    this.model.MinDelayBetween = value * 1000;
  }

  public onTypeChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.model.EventType = input.value;
  }

  public get isEditMode(): boolean {
    return (this.model?.Id ?? 0) > 0;
  }

  public getSelectedContainerName(): string {
    const containerId = this.model?.ContainerId;
    if (!containerId) return "";
    return this.containers.find(container => container.Id === containerId)?.Name ?? "";
  }

  public onContainerChanged() {
    this.syncSelectionsWithContainer();
  }

  public get filteredGroups(): IManagableObject[] {
    const containerId = this.model?.ContainerId;
    if (!containerId)
      return [];

    return this.groups.filter(group => group.ContainerId === containerId);
  }

  public get filteredGeozones(): GeozoneViewModel[] {
    const containerId = this.model?.ContainerId;
    if (!containerId)
      return [];

    return this.geozones.filter(zone => zone.ContainerId === containerId);
  }

  public get filteredGeozoneGroups(): ObjectGroupViewModel[] {
    const containerId = this.model?.ContainerId;
    if (!containerId)
      return [];

    return this.geozoneGroups.filter(group => group.ContainerId === containerId);
  }

  private ensureContainerSelected() {
    if (!this.model)
      return;

    if (this.model.ContainerId && this.containers.some(container => container.Id === this.model.ContainerId))
      return;

    this.model.ContainerId = this.containers[0]?.Id;
  }

  private syncSelectionsWithContainer() {
    const availableGroupIds = new Set(this.filteredGroups.map(group => group.Id));
    this.model.Groups = (this.model.Groups ?? []).filter(id => availableGroupIds.has(id));

    const availableGeozoneIds = new Set(this.filteredGeozones.map(zone => zone.Id));
    this.model.InGeozones = (this.model.InGeozones ?? []).filter(id => availableGeozoneIds.has(id));
    this.model.OutOfGeozones = (this.model.OutOfGeozones ?? []).filter(id => availableGeozoneIds.has(id));
  }

  public hasDuration() {
    return this.model.EventType == 'SPEED' ||
      this.model.EventType == 'NO_DATA' ||
      this.model.EventType == 'OLDCURPOS' ||
      this.model.EventType == 'PARK' ||
      this.model.EventType == 'MOVE' ||

      this.model.EventType == 'IDLING' ||
      this.model.EventType == 'TREND_OUT_OF_SCALE' ||

      this.model.EventType == 'EXITZONE' ||
      this.model.EventType == 'ENTERZONE' ||

      this.model.EventType == 'TREND_VALUE';
  }
  public hasPermittedInterval() {
    return this.model.EventType == 'SPEED' ||
      this.model.EventType == 'TREND_VALUE';
  }
}
